package com.arena_web.ui.sections.eventdetail.mobile

import androidx.compose.runtime.*
import com.arena_web.core.viewmodel.EventDetailUiState
import com.arena_web.core.viewmodel.UserParticipation
import com.arena_web.data.dto.EventDetailDto
import com.arena_web.data.dto.EventMediaDto
import com.arena_web.data.dto.EventParticipantDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.widgets.MediaLightbox
import com.arena_web.data.dto.EventTeamDto
import com.arena_web.ui.sections.eventdetail.CompletedStatusBadge
import com.arena_web.ui.sections.eventdetail.LeaderCard
import com.arena_web.ui.sections.eventdetail.LeaderStatKeysBySport
import com.arena_web.ui.sections.eventdetail.LeaderTeamBadge
import com.arena_web.ui.sections.eventdetail.buildLeaderEntries
import com.arena_web.ui.sections.eventdetail.MatchCenterTab
import com.arena_web.ui.sections.eventdetail.MatchCenterTabs
import com.arena_web.ui.sections.eventdetail.MobileRatePlayerSheet
import com.arena_web.ui.sections.eventdetail.PlayerCardKit
import com.arena_web.ui.sections.eventdetail.ProStatusBadge
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Video
import kotlin.js.Date
import kotlin.math.round

/**
 * Mobile COMPLETED event screen — Figma 8682:465139 (Match Info),
 * 8924:361443 (Lineups) and 9031:71550 (Media).
 *
 * Rendered by `EventDetailContentMobile` under its back row, so it inherits
 * the mobile `--scale` root. Layout: centred Match Info / Lineups / Media
 * segmented control (the desktop `MatchCenterTabs`, which reads mobile-sized
 * here), then the active tab's content filling the rest of the screen:
 *  - Match Info — the event photo as a full-height rounded card with a
 *    top-anchored gradient info overlay (Completed pill, title, date/time/
 *    location, format/players) and a chat FAB in the bottom-right corner.
 *  - Lineups — "THE MATCH ENDED" headline + a 3-per-row grid of player cards
 *    with the event-scoped community ★ average; tapping a card opens the
 *    Rate Performance popup. The stadium art behind comes from
 *    `AppPageLayout.backgroundImage` (wired in `Id.kt`).
 *  - Media — mosaic gallery (full-width row, then two 2-up rows, repeating)
 *    with tap-to-lightbox, video play badges and uploader/admin delete.
 */
@Composable
internal fun CompletedContentMobile(
    state: EventDetailUiState,
    activeTab: MatchCenterTab,
    onTabSelected: (MatchCenterTab) -> Unit,
    onBack: () -> Unit,
    onOpenChat: () -> Unit,
    onViewProfile: (String) -> Unit,
    onRatePlayer: (userId: String, attributeRatings: Map<String, Int>) -> Unit,
    onAddMedia: () -> Unit,
    onDeleteMedia: (String) -> Unit,
) {
    val event = state.event ?: return

    // Header — back button and the segmented control share one row. The back
    // button keeps the loading row's exact position (safe-area + s(12), left
    // s(12)) so it doesn't jump when the content mounts; the segments use the
    // tightest padding so all three tabs + the button fit a 390-px width.
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .flexShrink(0)
            .sPaddingBottom(14)
            .styleModifier {
                property("padding-top", "calc(env(safe-area-inset-top, 0px) + ${s(12)})")
                property("padding-left", s(12))
                property("padding-right", s(12))
                property("box-sizing", "border-box")
            },
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        CircleIconButton(icon = AppIcons.ChevronBack, onClick = onBack)
        MatchCenterTabs(
            activeTab = activeTab,
            onTabSelected = onTabSelected,
            segmentPaddingPx = 14,
        )
    }

    when (activeTab) {
        MatchCenterTab.Lineups -> MobileCompletedLineups(
            state = state,
            event = event,
            onViewProfile = onViewProfile,
            onRatePlayer = onRatePlayer,
        )
        MatchCenterTab.Media -> MobileCompletedMedia(
            state = state,
            // Same rule as the desktop AddMediaButtonRow / backend
            // UploadEventMediaUseCase — confirmed participants only.
            showAddButton = state.userParticipation == UserParticipation.CONFIRMED,
            onAddMedia = onAddMedia,
            onDeleteMedia = onDeleteMedia,
        )
        else ->
            if (event.isPro) {
                MobileCompletedMatchInfoPro(
                    state = state,
                    event = event,
                    onOpenChat = onOpenChat,
                    onViewProfile = onViewProfile,
                )
            } else {
                MobileCompletedMatchInfo(event = event, onOpenChat = onOpenChat)
            }
    }
}

// ── Match Info tab (Pro) ──────────────────────────────────────────────────────

/**
 * Pro completed Match Info — Figma 9155:246269 (+ empty leaderboards
 * 9288:160791 / 9288:162937): compact photo banner, per-team score row and
 * the two performance leaderboards (the desktop `PerformanceLeaders` logic
 * reused via `buildLeaderEntries` / `LeaderCard`). Chat floats bottom-right.
 */
@Composable
private fun MobileCompletedMatchInfoPro(
    state: EventDetailUiState,
    event: EventDetailDto,
    onOpenChat: () -> Unit,
    onViewProfile: (String) -> Unit,
) {
    val sportColors = LocalSportColors.current
    val t = LocalAppStrings.current
    val sportKey = event.sportName.lowercase()

    // Same per-sport stat pair resolution as desktop PerformanceLeaders.
    val playerStatTypes = state.playerStats?.statTypes
        ?.filter { it.scope.uppercase() == "PLAYER" }
        ?.sortedBy { it.displayOrder }
        ?: emptyList()
    val preferredKeys = LeaderStatKeysBySport[sportKey]
    val statType1 = preferredKeys
        ?.let { (key, _) -> playerStatTypes.find { it.displayKey == key } }
        ?: playerStatTypes.getOrNull(0)
    val statType2 = preferredKeys
        ?.let { (_, key) -> playerStatTypes.find { it.displayKey == key } }
        ?: playerStatTypes.getOrNull(1)

    val isRacketSport = sportKey == "tennis" || sportKey == "padel"
    val leftEmptyMessage = if (isRacketSport)
        t.eventLeadersEmptyWinGameShort
    else
        t.eventLeadersEmptyScoreFirst
    val rightEmptyMessage = if (isRacketSport)
        t.eventLeadersEmptyTakeSetShort
    else
        t.eventLeadersEmptyWinningAssist
    val rightStatIcon = if (isRacketSport) AppIcons.Trophy else AppIcons.Sight

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .flexGrow(1)
            .minHeight(0.px)
            .position(Position.Relative)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .fillMaxHeight()
                .sGap(16)
                .sPaddingTop(12)
                .styleModifier {
                    property("padding-left", "16px")
                    property("padding-right", "16px")
                    property("box-sizing", "border-box")
                    property("overflow-y", "auto")
                    property("scrollbar-width", "none")
                }
        ) {
            MobileProCompletedBanner(event = event)

            if (event.teams.isNotEmpty()) {
                MobileTeamsScoreRow(teams = event.teams)
            }

            if (statType1 != null && state.playerStats != null) {
                MobileLeaderPanel(
                    title = t.eventTopStat(statType1.name),
                    icon = AppIcons.Trophy,
                    statIcon = AppIcons.ballSmall(event.sportName),
                    entries = buildLeaderEntries(statType1, state.playerStats!!, event),
                    sport = event.sportName,
                    teams = event.teams,
                    background = "linear-gradient(17.1deg, rgba(0,59,174,0.03) 26.9%, rgba(3,87,230,0.03) 67.5%)",
                    emptyIcon = AppIcons.EmptyTopStrikers,
                    emptyMessage = leftEmptyMessage,
                    onViewProfile = onViewProfile,
                )
            }
            if (statType2 != null && state.playerStats != null) {
                MobileLeaderPanel(
                    title = t.eventTopStat(statType2.name),
                    icon = AppIcons.Lightning,
                    statIcon = rightStatIcon,
                    entries = buildLeaderEntries(statType2, state.playerStats!!, event),
                    sport = event.sportName,
                    teams = event.teams,
                    background = "linear-gradient(-27.4deg, rgba(20,26,32,0.12) 7.2%, rgba(10,36,68,0.2) 95.1%)",
                    emptyIcon = AppIcons.EmptyTopAssistants,
                    emptyMessage = rightEmptyMessage,
                    onViewProfile = onViewProfile,
                )
            }

            // Trailing spacer — scrolls the last panel clear of the chat FAB.
            Div(attrs = {
                style {
                    property("width", "100%")
                    property("height", "calc(${s(80)} + env(safe-area-inset-bottom))")
                    property("flex-shrink", "0")
                }
            }) {}
        }

        if (event.chatId != null) {
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .sSize(56)
                    .sBorderRadius(100)
                    .backgroundColor(sportColors.primary)
                    .cursor(Cursor.Pointer)
                    .styleModifier {
                        property("right", s(16))
                        property("bottom", "calc(${s(16)} + env(safe-area-inset-bottom))")
                        property("z-index", "3")
                    }
                    .onClick { onOpenChat() },
                contentAlignment = Alignment.Center,
            ) {
                Icon(
                    src = AppIcons.Chat,
                    modifier = Modifier.sSize(24).styleModifier {
                        property("filter", sportColors.iconFilterOnPrimary)
                    },
                )
            }
        }
    }
}

/** Compact photo banner (Figma 9155:246280) — title, stadium, date/time and
 *  format rows over the darkened event photo. */
@Composable
private fun MobileProCompletedBanner(event: EventDetailDto) {
    val t = LocalAppStrings.current
    val parsed = Date(event.dateTime)
    val months = arrayOf(
        t.eventMonthJanuary, t.eventMonthFebruary, t.eventMonthMarch, t.eventMonthApril,
        t.eventMonthMay, t.eventMonthJune, t.eventMonthJuly, t.eventMonthAugust,
        t.eventMonthSeptember, t.eventMonthOctober, t.eventMonthNovember, t.eventMonthDecember
    )
    val dateStr = "${parsed.getDate()} ${months[parsed.getMonth()]}"
    val timeStr = "${parsed.getHours().toString().padStart(2, '0')}:${
        parsed.getMinutes().toString().padStart(2, '0')
    }"
    val playersPerTeam = event.format ?: 0
    val totalPlayers = event.participants.count { it.status.uppercase() == "CONFIRMED" }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .flexShrink(0)
            .sBorderRadius(22)
            .overflow(Overflow.Hidden)
            .position(Position.Relative)
            .sGap(6)
            .sPadding(14, 16)
            .styleModifier {
                property(
                    "background",
                    "linear-gradient(97.36deg, rgba(0,0,0,0.75) 24.66%, rgba(0,0,0,0.2) 100%), " +
                        "rgba(255,255,255,0.24) " +
                        "url('${event.imageUrl ?: "/icons/event_football_field.png"}') center/cover no-repeat",
                )
                property("box-sizing", "border-box")
            }
    ) {
        SpanText(
            event.title.uppercase(),
            modifier = Modifier
                .sFontSize(24)
                .fontWeight(FontWeight.SemiBold)
                .sLineHeight(30)
                .color(AppColors.PrimaryWhite)
                .styleModifier {
                    property("letter-spacing", s(0.24))
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                    property("white-space", "nowrap")
                },
        )
        SpanText(
            event.address,
            modifier = Modifier
                .sFontSize(16)
                .sLineHeight(20)
                .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x99, 0xB1, 0xBF))
                .styleModifier {
                    property("letter-spacing", s(0.032))
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                    property("white-space", "nowrap")
                },
        )
        Row(
            modifier = Modifier.sGap(16).sMarginTop(6),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            InfoIconText(icon = AppIcons.Time, text = timeStr)
            InfoIconText(icon = AppIcons.Calendar, text = dateStr)
        }
        Row(
            modifier = Modifier.sGap(8).sMarginTop(2),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            InfoIconText(
                icon = AppIcons.ballSmall(event.sportName),
                text = t.eventFormatVs(playersPerTeam),
            )
            Row(modifier = Modifier.sGap(4), verticalAlignment = Alignment.CenterVertically) {
                Box(
                    modifier = Modifier
                        .sSize(4)
                        .sBorderRadius(100)
                        .backgroundColor(AppColors.Text.Secondary)
                )
                SpanText(
                    t.eventTeamsCount(event.teamsCount ?: event.teams.size),
                    modifier = Modifier.infoTextStyle(),
                )
            }
            InfoIconText(icon = AppIcons.People, text = t.eventPlayersCount(totalPlayers))
        }
    }
}

/** Per-team score strip (Figma 9155:246293) — team badge, name, score, with
 *  dash separators between teams. */
@Composable
private fun MobileTeamsScoreRow(teams: List<EventTeamDto>) {
    Row(
        modifier = Modifier.fillMaxWidth().flexShrink(0).sGap(16),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        teams.forEachIndexed { idx, team ->
            if (idx > 0) {
                Box(
                    modifier = Modifier
                        .sWidth(18)
                        .sHeight(1)
                        .styleModifier { property("background", "rgba(190,214,245,0.4)") }
                )
            }
            Column(
                modifier = Modifier.sGap(4),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                LeaderTeamBadge(
                    team = team,
                    modifier = Modifier.sSize(44).position(Position.Relative),
                )
                SpanText(
                    team.name.uppercase(),
                    modifier = Modifier
                        .sFontSize(14)
                        .fontWeight(FontWeight.Medium)
                        .sLineHeight(18)
                        .color(AppColors.PrimaryWhite)
                        .styleModifier { property("letter-spacing", s(0.028)) },
                )
                SpanText(
                    "${team.score}",
                    modifier = Modifier
                        .sFontSize(20)
                        .fontWeight(FontWeight.SemiBold)
                        .sLineHeight(24)
                        .color(AppColors.PrimaryWhite),
                )
            }
        }
    }
}

/** Mobile leaderboard panel — phone twin of the desktop `LeaderPanel`:
 *  header (title + icon), then a 4-per-row grid of `LeaderCard`s, or the
 *  emblem + message empty state. All rows render; the page scrolls. */
@Composable
private fun MobileLeaderPanel(
    title: String,
    icon: String,
    statIcon: String,
    entries: List<com.arena_web.ui.sections.eventdetail.LeaderEntry>,
    sport: String,
    teams: List<EventTeamDto>,
    background: String,
    emptyIcon: String,
    emptyMessage: String,
    onViewProfile: (String) -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .flexShrink(0)
            .sBorderRadius(22)
            .sPadding(16, 16)
            .sGap(16)
            .styleModifier {
                property("border", "1px solid rgba(41,52,138,0.2)")
                property("background", background)
                property("box-sizing", "border-box")
            }
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(
                title,
                modifier = Modifier
                    .sFontSize(18)
                    .fontWeight(FontWeight.Medium)
                    .sLineHeight(24)
                    .color(AppColors.Text.Secondary)
                    .styleModifier { property("letter-spacing", s(0.09)) },
            )
            Icon(src = icon, modifier = Modifier.sSize(20))
        }

        if (entries.isEmpty()) {
            Column(
                modifier = Modifier.fillMaxWidth().sGap(24).sPadding(24, 0),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Image(
                    src = emptyIcon,
                    modifier = Modifier
                        .sHeight(120)
                        .styleModifier { property("object-fit", "contain") },
                )
                SpanText(
                    emptyMessage,
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(FontWeight.Medium)
                        .sLineHeight(20)
                        .color(AppColors.Text.Secondary)
                        .styleModifier {
                            property("letter-spacing", s(0.032))
                            property("text-align", "center")
                        },
                )
            }
        } else {
            // 4-per-row grid of the desktop LeaderCards — they fill their
            // grid cell at the FIFA aspect via container queries. Raw Div:
            // Kobweb Box is itself a single-cell grid and would stack them.
            Div(attrs = {
                style {
                    property("width", "100%")
                    property("display", "grid")
                    property("grid-template-columns", "repeat(4, 1fr)")
                    property("column-gap", s(12))
                    property("row-gap", s(12))
                }
            }) {
                entries.forEach { entry ->
                    LeaderCard(
                        entry = entry,
                        statIcon = statIcon,
                        sport = sport,
                        teams = teams,
                        onViewProfile = onViewProfile,
                    )
                }
            }
        }
    }
}

// ── Match Info tab ────────────────────────────────────────────────────────────

@Composable
private fun MobileCompletedMatchInfo(
    event: EventDetailDto,
    onOpenChat: () -> Unit,
) {
    val sportColors = LocalSportColors.current
    val t = LocalAppStrings.current

    val parsed = Date(event.dateTime)
    val months = arrayOf(
        t.eventMonthJanuary, t.eventMonthFebruary, t.eventMonthMarch, t.eventMonthApril,
        t.eventMonthMay, t.eventMonthJune, t.eventMonthJuly, t.eventMonthAugust,
        t.eventMonthSeptember, t.eventMonthOctober, t.eventMonthNovember, t.eventMonthDecember
    )
    val dateStr = "${parsed.getDate()} ${months[parsed.getMonth()]}"
    val timeStr = "${parsed.getHours().toString().padStart(2, '0')}:${
        parsed.getMinutes().toString().padStart(2, '0')
    }"
    val playersPerTeam = event.format ?: 0
    val totalPlayers = event.participants.count { it.status.uppercase() == "CONFIRMED" }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .flexGrow(1)
            .minHeight(0.px)
            .sPaddingTop(12)
            .styleModifier {
                property("padding-left", "16px")
                property("padding-right", "16px")
                property("padding-bottom", "calc(16px + env(safe-area-inset-bottom))")
                property("box-sizing", "border-box")
            }
    ) {
        // Photo card — fills the remaining screen height, rounded 22.
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .flexGrow(1)
                .minHeight(0.px)
                .position(Position.Relative)
                .sBorderRadius(22)
                .overflow(Overflow.Hidden)
                .styleModifier {
                    property(
                        "background",
                        "rgba(255,255,255,0.24) " +
                            "url('${event.imageUrl ?: "/icons/event_football_field.png"}') center/cover no-repeat",
                    )
                }
        ) {
            // Info card — mobile twin of the desktop CompletedMatchBanner's
            // overlay card: semi-transparent fill + blur, gradient hairline
            // via the mask-composite trick, title with the PRO+/Completed
            // badge stack top-right, then date/time/location and format rows.
            Column(
                modifier = Modifier
                    .position(Position.Absolute)
                    .sBorderRadius(22)
                    .sPadding(18, 16)
                    .sGap(24)
                    .styleModifier {
                        property("left", s(12))
                        property("right", s(12))
                        property("top", s(12))
                        property("background", "rgba(2, 4, 11, 0.4)")
                        property("backdrop-filter", "blur(4px)")
                        property("box-sizing", "border-box")
                    }
            ) {
                Box(
                    modifier = Modifier.styleModifier {
                        property("position", "absolute")
                        property("inset", "0")
                        property("border", "0.3px solid transparent")
                        property("border-radius", s(22))
                        property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                        property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                        property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                        property("-webkit-mask-composite", "xor")
                        property("mask-composite", "exclude")
                        property("pointer-events", "none")
                    }
                )

                // Title + badge stack. The badges sit in an absolute column at
                // the top-right (same trick as desktop) so their taller stack
                // doesn't push the info rows down; the title reserves room.
                Box(modifier = Modifier.fillMaxWidth().position(Position.Relative)) {
                    SpanText(
                        event.title.uppercase(),
                        modifier = Modifier
                            .fillMaxWidth()
                            .sPaddingRight(110)
                            .sFontSize(20)
                            .fontWeight(FontWeight.SemiBold)
                            .sLineHeight(24)
                            .color(AppColors.Text.Secondary)
                            .styleModifier {
                                property("letter-spacing", s(0.2))
                                property("box-sizing", "border-box")
                            },
                    )
                    Column(
                        modifier = Modifier
                            .sGap(8)
                            .position(Position.Absolute)
                            .styleModifier {
                                property("top", "0")
                                property("right", "0")
                            },
                        horizontalAlignment = Alignment.End,
                    ) {
                        if (event.isPro) {
                            ProStatusBadge(sportColors = sportColors)
                        }
                        CompletedStatusBadge()
                    }
                }

                // Main information — same grouping as desktop.
                Column(modifier = Modifier.sGap(16)) {
                    Column(modifier = Modifier.sGap(8)) {
                        Row(modifier = Modifier.sGap(16), verticalAlignment = Alignment.CenterVertically) {
                            InfoIconText(icon = AppIcons.Time, text = timeStr)
                            InfoIconText(icon = AppIcons.Calendar, text = dateStr)
                        }
                        InfoIconText(icon = AppIcons.Location, text = event.address, ellipsis = true)
                    }

                    Column(modifier = Modifier.sGap(8)) {
                        Row(modifier = Modifier.sGap(8), verticalAlignment = Alignment.CenterVertically) {
                            InfoIconText(
                                icon = AppIcons.ballSmall(event.sportName),
                                text = t.eventFormatVs(playersPerTeam),
                            )
                            // Teams count — Pro only, same rule as the desktop
                            // CompletedMatchBanner (non-Pro has no squads).
                            if (event.isPro) {
                                Row(modifier = Modifier.sGap(4), verticalAlignment = Alignment.CenterVertically) {
                                    Box(
                                        modifier = Modifier
                                            .sSize(5)
                                            .sBorderRadius(100)
                                            .backgroundColor(AppColors.Text.Secondary)
                                    )
                                    SpanText(
                                        t.eventTeamsCount(event.teamsCount ?: event.teams.size),
                                        modifier = Modifier.infoTextStyle(),
                                    )
                                }
                            }
                        }
                        InfoIconText(icon = AppIcons.People, text = t.eventPlayersCount(totalPlayers))
                    }
                }
            }

            // Chat FAB — bottom-right over the photo.
            if (event.chatId != null) {
                Box(
                    modifier = Modifier
                        .position(Position.Absolute)
                        .sSize(56)
                        .sBorderRadius(100)
                        .backgroundColor(sportColors.primary)
                        .cursor(Cursor.Pointer)
                        .styleModifier {
                            property("right", s(16))
                            property("bottom", s(16))
                            property("z-index", "2")
                        }
                        .onClick { onOpenChat() },
                    contentAlignment = Alignment.Center,
                ) {
                    Icon(
                        src = AppIcons.Chat,
                        modifier = Modifier.sSize(24).styleModifier {
                            property("filter", sportColors.iconFilterOnPrimary)
                        },
                    )
                }
            }
        }
    }
}

private fun Modifier.infoTextStyle(): Modifier = this
    .sFontSize(16)
    .fontWeight(FontWeight.Medium)
    .sLineHeight(18)
    .color(AppColors.Text.Secondary)
    .styleModifier { property("letter-spacing", s(0.032)) }

@Composable
private fun InfoIconText(icon: String, text: String, ellipsis: Boolean = false) {
    Row(modifier = Modifier.sGap(8), verticalAlignment = Alignment.CenterVertically) {
        Icon(src = icon, modifier = Modifier.sSize(16).flexShrink(0))
        SpanText(
            text,
            modifier = Modifier
                .infoTextStyle()
                .thenIfEllipsis(ellipsis),
        )
    }
}

private fun Modifier.thenIfEllipsis(enabled: Boolean): Modifier =
    if (enabled) styleModifier {
        property("white-space", "nowrap")
        property("overflow", "hidden")
        property("text-overflow", "ellipsis")
        property("min-width", "0")
    } else this

// ── Lineups tab ───────────────────────────────────────────────────────────────

@Composable
private fun MobileCompletedLineups(
    state: EventDetailUiState,
    event: EventDetailDto,
    onViewProfile: (String) -> Unit,
    onRatePlayer: (userId: String, attributeRatings: Map<String, Int>) -> Unit,
) {
    val t = LocalAppStrings.current
    val sportName = event.sportName ?: "football"
    val confirmed = event.participants.filter { it.status.uppercase() == "CONFIRMED" }

    // Event-scoped community ★ average per player — same aggregation as the
    // desktop Lineups tab (MatchView.LineupsFieldDisplay).
    val eventRatingAverages: Map<String, Double> = remember(state.ratings) {
        state.ratings
            .groupBy { it.ratedUserId }
            .mapValues { (_, list) ->
                list.mapNotNull { it.generalRating.toDoubleOrNull() }
                    .takeIf { it.isNotEmpty() }
                    ?.average()
                    ?: 0.0
            }
            .filterValues { it > 0.0 }
    }

    var ratedPlayer by remember { mutableStateOf<EventParticipantDto?>(null) }

    // Relative host — the ambient pitch backdrop paints behind the headline
    // and card grid, same treatment as the live match view (MatchViewMobile):
    // sport pitch art contained to the viewport width, dissolved into the
    // body composite with a vertical fade mask.
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .flexGrow(1)
            .minHeight(0.px)
            .position(Position.Relative)
    ) {
        Box(
            modifier = Modifier
                .position(Position.Absolute)
                .styleModifier {
                    property("inset", "0")
                    property("pointer-events", "none")
                }
        ) {
            org.jetbrains.compose.web.dom.Img(
                src = AppIcons.mobileEventBackground(sportName),
                attrs = Modifier.styleModifier {
                    property("position", "absolute")
                    property("inset", "0")
                    property("width", "100%")
                    property("height", "100%")
                    property("object-fit", "contain")
                    property("object-position", "center 0%")
                    property("pointer-events", "none")
                    val mask = "linear-gradient(180deg, " +
                        "transparent 0%, " +
                        "black 32%, " +
                        "black 78%, " +
                        "transparent 100%)"
                    property("mask-image", mask)
                    property("-webkit-mask-image", mask)
                }.toAttrs()
            )
        }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .fillMaxHeight()
            .position(Position.Relative)
            .sGap(24)
            .sPaddingTop(12)
            .styleModifier {
                property("padding-left", "16px")
                property("padding-right", "16px")
                property("box-sizing", "border-box")
                property("z-index", "1")
            },
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        // Headline (Figma 8924:361795 — mobile-sized MatchEndedHeader).
        Column(
            modifier = Modifier.sGap(14),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SpanText(
                t.eventMatchEndedTitle,
                modifier = Modifier
                    .sFontSize(24)
                    .fontWeight(FontWeight.SemiBold)
                    .sLineHeight(30)
                    .color(AppColors.Text.Secondary)
                    .styleModifier {
                        property("letter-spacing", s(0.72))
                        property("text-align", "center")
                    },
            )
            SpanText(
                t.eventRatePlayersSubtitle,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(FontWeight.Medium)
                    .sLineHeight(18)
                    .color(AppColors.Text.Secondary)
                    .styleModifier {
                        property("letter-spacing", s(0.032))
                        property("text-align", "center")
                    },
            )
        }

        // 3-per-row card grid, scrolls under the headline.
        Div(attrs = {
            style {
                property("width", "100%")
                property("flex", "1")
                property("min-height", "0")
                property("display", "flex")
                property("flex-wrap", "wrap")
                property("gap", "${s(12)} ${s(18)}")
                property("justify-content", "center")
                property("align-content", "flex-start")
                property("overflow-y", "auto")
                property("scrollbar-width", "none")
            }
        }) {
            confirmed.forEach { p ->
                MobileRatedPlayerCard(
                    participant = p,
                    sportName = sportName,
                    playerStats = state.playerStats,
                    // Non-Pro events are casual sessions without structured
                    // scoring — no goal/assist tiles on their cards (same as
                    // the desktop NonProCompletedLineups grid).
                    showStatBlocks = event.isPro,
                    ratingAverage = eventRatingAverages[p.userId],
                    onTap = { ratedPlayer = p },
                )
            }
            // Trailing spacer — padding-bottom on a scroll container is
            // dropped at scroll-end on Chrome/Safari, so a sized element
            // provides the bottom gap instead.
            Div(attrs = {
                style {
                    property("width", "100%")
                    property("height", "calc(${s(8)} + env(safe-area-inset-bottom))")
                    property("flex-shrink", "0")
                }
            }) {}
        }
    }
    }

    // Rate Performance popup — the desktop popup rendered at a reduced local
    // scale so its two-column card fits the phone width (the screen root sets
    // --scale to the mobile baseline, which would blow it up past 390px).
    ratedPlayer?.let { player ->
        val viewerId = Storage.get(StorageKeys.USER_ID)
        val previousReview = state.ratings.firstOrNull {
            it.ratedUserId == player.userId && it.reviewerId == viewerId
        }
        val previousByAttribute = previousReview
            ?.details
            ?.associate { it.attributeId to (it.value.toDoubleOrNull()?.toInt() ?: 0) }
            ?: emptyMap()
        val playerRatings = state.ratings.filter { it.ratedUserId == player.userId }
        val communityAvg = playerRatings
            .mapNotNull { it.generalRating.toDoubleOrNull() }
            .takeIf { it.isNotEmpty() }
            ?.average()

        // Close after a submit only once the POST resolves (same pattern as
        // the desktop Lineups tab).
        var submitTriggered by remember(player.userId) { mutableStateOf(false) }
        LaunchedEffect(state.ratingSubmitInProgress, submitTriggered) {
            if (submitTriggered && !state.ratingSubmitInProgress) {
                ratedPlayer = null
                submitTriggered = false
            }
        }

        MobileRatePlayerSheet(
            player = player,
            sport = sportName,
            initialRatings = previousByAttribute,
            communityAverage = communityAvg,
            communityCount = playerRatings.size,
            isSubmitting = state.ratingSubmitInProgress,
            isSelf = viewerId != null && viewerId == player.userId,
            playerStats = state.playerStats,
            showStatBlocks = event.isPro,
            onViewProfile = { uid ->
                // Close the rate sheet first, then open the profile sheet.
                ratedPlayer = null
                onViewProfile(uid)
            },
            onClose = {
                if (!state.ratingSubmitInProgress) ratedPlayer = null
            },
            onSubmit = { ratings ->
                onRatePlayer(player.userId, ratings)
                submitTriggered = true
            },
        )
    }
}

@Composable
private fun MobileRatedPlayerCard(
    participant: EventParticipantDto,
    sportName: String,
    playerStats: com.arena_web.data.dto.EventPlayerStatsResultDto?,
    showStatBlocks: Boolean,
    ratingAverage: Double?,
    onTap: () -> Unit,
) {
    // Fixed Figma slot (8924:362901 — 84-wide cards, 18 gap → a 288-wide
    // centred trio). Inside, the desktop `PlayerCardKit` (card + read-only
    // corner StatBlocks + ★ average + name) renders at its intrinsic
    // XtraSmall 58×90 and CSS-scales up to fill the slot — the exact same
    // recipe the desktop completed Lineups uses, so goals/assists tiles and
    // the rating overlay match 1:1.
    Box(
        modifier = Modifier
            .position(Position.Relative)
            .styleModifier {
                property("width", s(84))
                property("aspect-ratio", "94 / 147")
                property("flex-shrink", "0")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
            }
    ) {
        PlayerCardKit(
            player = participant,
            cardSize = PlayerCardSize.XtraSmall,
            cardScale = 84.0 / 58.0,
            sport = sportName,
            showStatBlocks = showStatBlocks,
            isAdmin = false,
            playerStats = playerStats,
            showRating = true,
            hideZeroStats = true,
            onPlayerClick = { onTap() },
            eventRatingAverage = ratingAverage,
        )
    }
}

// ── Media tab ─────────────────────────────────────────────────────────────────

/** One cell of the media mosaic — either a real item or an in-flight upload. */
private sealed interface MediaCell
private data class MediaItemCell(val media: EventMediaDto, val galleryIndex: Int) : MediaCell
private data class MediaPendingCell(val upload: com.arena_web.core.viewmodel.PendingMediaUpload) : MediaCell

@Composable
private fun MobileCompletedMedia(
    state: EventDetailUiState,
    showAddButton: Boolean,
    onAddMedia: () -> Unit,
    onDeleteMedia: (String) -> Unit,
) {
    val t = LocalAppStrings.current
    val currentUserId = Storage.get(StorageKeys.USER_ID)
    // Newest first — the grid reads from the latest upload down to the
    // oldest, and fresh uploads land at the top.
    val sortedItems = remember(state.mediaItems) {
        state.mediaItems.sortedByDescending { it.createdAt }
    }
    // Index into sortedItems of the tile the fullscreen gallery opened on;
    // null = gallery closed.
    var galleryIndex by remember { mutableStateOf<Int?>(null) }

    // Scroll handle — jumps the list back to the top when a fresh upload
    // batch starts, so its loader tiles are immediately visible.
    val scrollHost = remember { mutableStateOf<org.w3c.dom.HTMLElement?>(null) }
    LaunchedEffect(state.pendingMediaUploads.isNotEmpty()) {
        if (state.pendingMediaUploads.isNotEmpty()) scrollHost.value?.scrollTop = 0.0
    }

    // Relative host — the scrollable mosaic fills it, the add-media FAB
    // floats bottom-right above the tiles.
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .flexGrow(1)
            .minHeight(0.px)
            .position(Position.Relative)
    ) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .fillMaxHeight()
            .sGap(12)
            .sPaddingTop(12)
            .styleModifier {
                property("padding-left", "16px")
                property("padding-right", "16px")
                property("box-sizing", "border-box")
                property("overflow-y", "auto")
                property("scrollbar-width", "none")
            }
            .attrsModifier {
                ref { el ->
                    scrollHost.value = el.unsafeCast<org.w3c.dom.HTMLElement>()
                    onDispose { scrollHost.value = null }
                }
            }
    ) {
        // Uploading tiles first, then the uploaded media newest-first.
        val cells: List<MediaCell> =
            state.pendingMediaUploads.map { MediaPendingCell(it) } +
                sortedItems.mapIndexed { idx, m -> MediaItemCell(m, idx) }

        when {
            state.mediaLoading && cells.isEmpty() -> Box(
                modifier = Modifier.fillMaxWidth().flexGrow(1),
                contentAlignment = Alignment.Center,
            ) {
                SpanText(
                    t.eventLoadingMedia,
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(FontWeight.Medium)
                        .color(AppColors.Text.Secondary),
                )
            }
            cells.isEmpty() -> Column(
                modifier = Modifier.fillMaxWidth().flexGrow(1).sGap(16),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center,
            ) {
                Icon(src = AppIcons.Videocam, modifier = Modifier.sSize(56))
                SpanText(
                    t.eventNoMediaYet,
                    modifier = Modifier
                        .sFontSize(20)
                        .fontWeight(FontWeight.Medium)
                        .color(AppColors.Text.Secondary),
                )
                SpanText(
                    t.eventNoMediaDescription,
                    modifier = Modifier
                        .sFontSize(14)
                        .fontWeight(FontWeight.Medium)
                        .color(AppColors.Text.Muted)
                        .styleModifier { property("text-align", "center") },
                )
            }
            else -> {
                // Mosaic pattern per Figma 9031:71571 — one full-width tile,
                // then two rows of side-by-side pairs, repeating.
                val chunks = buildList {
                    var i = 0
                    var step = 0
                    while (i < cells.size) {
                        val take = if (step % 3 == 0) 1 else 2
                        add(cells.subList(i, minOf(i + take, cells.size)).toList())
                        i += take
                        step++
                    }
                }

                @Composable
                fun renderCell(cell: MediaCell, modifier: Modifier) {
                    when (cell) {
                        is MediaPendingCell -> MobileUploadingMediaTile(
                            upload = cell.upload,
                            modifier = modifier,
                        )
                        is MediaItemCell -> MobileMediaTile(
                            media = cell.media,
                            currentUserId = currentUserId,
                            isAdmin = state.isAdmin,
                            onDelete = onDeleteMedia,
                            onOpen = { galleryIndex = cell.galleryIndex },
                            modifier = modifier,
                        )
                    }
                }

                chunks.forEach { chunk ->
                    if (chunk.size == 1) {
                        renderCell(chunk[0], Modifier.fillMaxWidth().sHeight(197).flexShrink(0))
                    } else {
                        Row(modifier = Modifier.fillMaxWidth().sGap(12).flexShrink(0)) {
                            chunk.forEach { cell ->
                                renderCell(
                                    cell,
                                    Modifier
                                        .sHeight(170)
                                        .minWidth(0.px)
                                        .styleModifier { property("flex", "1 1 0px") },
                                )
                            }
                        }
                    }
                }
                // Trailing spacer — see feedback: padding-bottom on a flex
                // scroll container is dropped at scroll-end. Tall enough to
                // scroll the last tiles clear of the add-media FAB.
                Div(attrs = {
                    style {
                        property("width", "100%")
                        property("height", "calc(${s(80)} + env(safe-area-inset-bottom))")
                        property("flex-shrink", "0")
                    }
                }) {}
            }
        }
    }

    // Add-media FAB — floats over the mosaic (the header row has no room
    // for it next to the back button + tabs).
    if (showAddButton) {
        val sportColors = LocalSportColors.current
        Box(
            modifier = Modifier
                .position(Position.Absolute)
                .sSize(56)
                .sBorderRadius(100)
                .backgroundColor(sportColors.primary)
                .cursor(Cursor.Pointer)
                .styleModifier {
                    property("right", s(16))
                    property("bottom", "calc(${s(16)} + env(safe-area-inset-bottom))")
                    property("z-index", "3")
                }
                .onClick { onAddMedia() },
            contentAlignment = Alignment.Center,
        ) {
            Icon(
                src = AppIcons.Add,
                modifier = Modifier.sSize(20).styleModifier {
                    property("filter", sportColors.iconFilterOnPrimary)
                },
            )
        }
    }
    }

    galleryIndex?.let { idx ->
        MobileMediaGalleryViewer(
            items = sortedItems,
            startIndex = idx.coerceIn(0, (sortedItems.size - 1).coerceAtLeast(0)),
            onDismiss = { galleryIndex = null },
        )
    }

}

/** Loader tile for an in-flight upload — dimmed local preview + spinner. */
@Composable
private fun MobileUploadingMediaTile(
    upload: com.arena_web.core.viewmodel.PendingMediaUpload,
    modifier: Modifier = Modifier,
) {
    Box(
        modifier = modifier
            .sBorderRadius(12)
            .overflow(Overflow.Hidden)
            .position(Position.Relative)
            .styleModifier { property("background-color", "#0B1A38") },
        contentAlignment = Alignment.Center,
    ) {
        if (upload.isVideo) {
            Video(attrs = {
                attr("src", "${upload.previewUrl}#t=0.1")
                attr("preload", "metadata")
                attr("muted", "")
                attr("playsinline", "")
                style {
                    property("position", "absolute")
                    property("inset", "0")
                    property("width", "100%")
                    property("height", "100%")
                    property("object-fit", "cover")
                    property("pointer-events", "none")
                    property("opacity", "0.5")
                }
            })
        } else {
            Image(
                src = upload.previewUrl,
                modifier = Modifier
                    .fillMaxSize()
                    .styleModifier {
                        property("object-fit", "cover")
                        property("opacity", "0.5")
                    },
            )
        }
        Box(modifier = Modifier.zIndex(1)) {
            com.arena_web.ui.components.widgets.LoadingSpinner(size = 32.px)
        }
    }
}

@Composable
private fun MobileMediaTile(
    media: EventMediaDto,
    currentUserId: String?,
    isAdmin: Boolean,
    onDelete: (String) -> Unit,
    onOpen: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val t = LocalAppStrings.current
    val isVideo = media.mediaType.uppercase() == "VIDEO"
    val canDelete = isAdmin || (currentUserId != null && media.uploaderId == currentUserId)
    var confirmDelete by remember { mutableStateOf(false) }

    Box(
        modifier = modifier
            .sBorderRadius(12)
            .overflow(Overflow.Hidden)
            .position(Position.Relative)
            .cursor(Cursor.Pointer)
            .onClick { onOpen() },
        contentAlignment = Alignment.Center,
    ) {
        if (isVideo) {
            // `#t=0.1` seeks 100 ms in so the first frame paints as the
            // poster (same trick as the desktop MatchMedia tiles).
            Video(attrs = {
                attr("src", "${media.url}#t=0.1")
                attr("preload", "metadata")
                attr("muted", "")
                attr("playsinline", "")
                style {
                    property("position", "absolute")
                    property("inset", "0")
                    property("width", "100%")
                    property("height", "100%")
                    property("object-fit", "cover")
                    property("pointer-events", "none")
                }
            })
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .sSize(50)
                    .borderRadius(50.percent)
                    .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgba(0, 0, 0, 0.45f))
                    .zIndex(1),
                contentAlignment = Alignment.Center,
            ) {
                Icon(src = AppIcons.Play, modifier = Modifier.sSize(22))
            }
        } else {
            Image(
                src = media.url,
                modifier = Modifier
                    .fillMaxSize()
                    .styleModifier { property("object-fit", "cover") },
            )
        }

        if (canDelete) {
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .sTop(8)
                    .sRight(8)
                    .sSize(32)
                    .borderRadius(50.percent)
                    .cursor(Cursor.Pointer)
                    .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgba(0, 0, 0, 0.55f))
                    .zIndex(2)
                    .onClick {
                        it.stopPropagation()
                        confirmDelete = true
                    },
                contentAlignment = Alignment.Center,
            ) {
                Icon(src = AppIcons.Trash, modifier = Modifier.sSize(16))
            }
        }
    }

    com.arena_web.ui.components.events.MatchActionConfirmDialog(
        isOpen = confirmDelete,
        title = t.eventDeleteMediaQuestion,
        message = t.eventDeleteMediaMessage,
        confirmText = t.commonDelete,
        iconSrc = AppIcons.Trash,
        iconAccent = "#F72A0A",
        isBusy = false,
        busyText = t.eventDeleting,
        onCancel = { confirmDelete = false },
        onConfirm = {
            onDelete(media.id)
            confirmDelete = false
        },
    )
}

/**
 * Fullscreen swipeable media gallery — opens from a tile tap. Stories-style
 * segment strip at the top (one bar per media item, filled up to the current
 * one), native scroll-snap carousel below: each slide is a full-viewport
 * page, so flicking left/right pages through the event's media with browser
 * momentum/snapping — no JS drag maths. Videos pause when swiped away.
 */
@Composable
private fun MobileMediaGalleryViewer(
    items: List<EventMediaDto>,
    startIndex: Int,
    onDismiss: () -> Unit,
) {
    var currentIndex by remember { mutableStateOf(startIndex) }

    Box(
        modifier = Modifier.styleModifier {
            property("position", "fixed")
            property("inset", "0")
            property("z-index", "9999")
            property("background", "rgba(0, 0, 0, 0.96)")
        }
    ) {
        // ── Scroll-snap track ──
        Div(attrs = {
            style {
                property("position", "absolute")
                property("inset", "0")
                property("display", "flex")
                property("overflow-x", "auto")
                property("overflow-y", "hidden")
                property("scroll-snap-type", "x mandatory")
                property("scrollbar-width", "none")
                property("-webkit-overflow-scrolling", "touch")
            }
            ref { el ->
                // Land on the tapped tile before the first paint settles.
                kotlinx.browser.window.requestAnimationFrame {
                    el.scrollLeft = el.clientWidth.toDouble() * startIndex
                }
                val onScroll: (org.w3c.dom.events.Event) -> Unit = {
                    val w = el.clientWidth.toDouble().coerceAtLeast(1.0)
                    val idx = kotlin.math.round(el.scrollLeft / w).toInt()
                        .coerceIn(0, items.size - 1)
                    if (idx != currentIndex) {
                        currentIndex = idx
                        // Swiping away from a playing video pauses it.
                        el.querySelectorAll("video").let { list ->
                            for (i in 0 until list.length) {
                                (list.item(i) as? org.w3c.dom.HTMLVideoElement)?.pause()
                            }
                        }
                    }
                }
                el.addEventListener("scroll", onScroll)
                onDispose { el.removeEventListener("scroll", onScroll) }
            }
        }) {
            items.forEach { media ->
                Div(attrs = {
                    style {
                        property("flex", "0 0 100%")
                        property("width", "100%")
                        property("height", "100%")
                        property("scroll-snap-align", "center")
                        property("scroll-snap-stop", "always")
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("box-sizing", "border-box")
                        property("padding", "calc(env(safe-area-inset-top, 0px) + ${s(56)}) 0 " +
                            "calc(env(safe-area-inset-bottom, 0px) + ${s(24)}) 0")
                    }
                }) {
                    if (media.mediaType.uppercase() == "VIDEO") {
                        Video(attrs = {
                            attr("src", "${media.url}#t=0.1")
                            attr("controls", "")
                            attr("playsinline", "")
                            attr("preload", "metadata")
                            style {
                                property("max-width", "100%")
                                property("max-height", "100%")
                                property("display", "block")
                            }
                        })
                    } else {
                        org.jetbrains.compose.web.dom.Img(
                            src = media.url,
                            attrs = {
                                style {
                                    property("max-width", "100%")
                                    property("max-height", "100%")
                                    property("object-fit", "contain")
                                    property("display", "block")
                                }
                            }
                        )
                    }
                }
            }
        }

        // ── Top chrome: stories-style segment strip + close ──
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .position(Position.Absolute)
                .sGap(10)
                .styleModifier {
                    property("top", "0")
                    property("left", "0")
                    property("padding",
                        "calc(env(safe-area-inset-top, 0px) + ${s(10)}) ${s(12)} ${s(12)} ${s(12)}")
                    property("box-sizing", "border-box")
                    property("background",
                        "linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 100%)")
                    property("z-index", "2")
                    property("pointer-events", "none")
                }
        ) {
            Row(modifier = Modifier.fillMaxWidth().sGap(4)) {
                items.indices.forEach { i ->
                    Box(
                        modifier = Modifier
                            .sHeight(3)
                            .sBorderRadius(100)
                            .styleModifier {
                                property("flex", "1 1 0px")
                                property(
                                    "background",
                                    if (i <= currentIndex) "#FFFFFF" else "rgba(255,255,255,0.35)",
                                )
                                property("transition", "background 0.2s ease")
                            }
                    ) {}
                }
            }
        }

        // Close — top-right, above the strip's gradient.
        Box(
            modifier = Modifier
                .sSize(36)
                .sBorderRadius(100)
                .cursor(Cursor.Pointer)
                .position(Position.Absolute)
                .styleModifier {
                    property("top", "calc(env(safe-area-inset-top, 0px) + ${s(22)})")
                    property("right", s(12))
                    property("background", "rgba(255,255,255,0.16)")
                    property("z-index", "3")
                }
                .onClick { onDismiss() },
            contentAlignment = Alignment.Center,
        ) {
            SpanText(
                "✕",
                modifier = Modifier.sFontSize(16).color(AppColors.PrimaryWhite),
            )
        }
    }
}
